import { useQuery } from '@tanstack/react-query';
import { LambdaInvocationTypes } from '../../primary-record-types';
import { publicAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useEffect, useState } from 'react';
import { getPublicSharedReportCacheKey } from './cacheKeys';
import { getIntl } from '../../utils/intl';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useNotify from '../useNotify';

const intl = getIntl('usePublicSharedReports', {
    errorLoadingSharedReport: 'Error occurred processing report',
    loadReportSuccess: 'Successfully processed report',
});

export type UsePublicSharedReportsArgs = {
    shareCode: string;
};

export type UsePublicSharedReportReturnValue = {
    response: LambdaInvocationTypes.GenerateSharedReportResponse;
};

export const usePublicSharedReports = ({ shareCode }: UsePublicSharedReportsArgs): UsePublicSharedReportReturnValue => {
    const [lastResponse, setLastResponse] = useState<LambdaInvocationTypes.GenerateSharedReportResponse>({
        status: 'PROCESSING',
    });
    const notify = useNotify();

    const query = useQuery({
        queryKey: getPublicSharedReportCacheKey(shareCode),
        queryFn: async () => {
            const response = await publicAPI.get<LambdaInvocationTypes.GenerateSharedReportResponse>(
                urls.api.publicSharedReports(shareCode),
            );

            return response;
        },
        refetchInterval: lastResponse.status === 'PROCESSING' ? 1000 : false,
    });

    useQueryErrorNotify(query, intl('errorLoadingSharedReport'));

    useEffect(() => {
        if (query.data) {
            setLastResponse(query.data);
        }

        if (query.data?.status === 'COMPLETE' && lastResponse.status === 'PROCESSING') {
            notify('success', intl('loadReportSuccess'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.data]);

    return {
        response: lastResponse,
    };
};
